import { useNavigate } from 'react-router-dom';
import { Scissors, Mail, Instagram, Facebook, Youtube } from 'lucide-react';

const Footer = ({ dark = false }) => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const linkStyle = {
    color: dark ? '#9ca3af' : '#6b7280',
    fontSize: '0.8rem',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
  };

  const headingStyle = {
    color: dark ? '#f3f4f6' : '#111827',
    fontSize: '0.82rem',
    fontWeight: 700,
    marginBottom: '10px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };

  const socialLinks = [
    { icon: <Instagram size={17} />, label: 'Instagram', url: 'https://instagram.com/bookyoursalons', bg: 'linear-gradient(135deg,#f58529,#dd2a7b,#8134af,#515bd4)' },
    { icon: <Facebook size={17} />, label: 'Facebook', url: 'https://facebook.com/bookyoursalons', bg: '#1877f2' },
    { icon: <Youtube size={17} />, label: 'YouTube', url: 'https://youtube.com/@bookyoursalons', bg: '#ff0000' },
  ];

  return (
    <footer style={{
      background: dark
        ? 'linear-gradient(135deg, #0f0f1a, #1a1025)'
        : 'linear-gradient(135deg, #111827, #1f2937)',
      borderTop: dark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,255,255,0.08)',
      padding: '40px 20px 24px',
      marginTop: '32px',
      position: 'relative',
      zIndex: 10,
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Top section */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '28px', marginBottom: '28px' }}>

          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #dc2626, #e11d48)',
                padding: '6px', borderRadius: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Scissors size={14} color="white" />
              </div>
              <span style={{ color: '#f9fafb', fontWeight: 800, fontSize: '0.95rem', fontFamily: "'Syne', sans-serif" }}>
                BookYourSalons
              </span>
            </div>
            <p style={{ color: '#9ca3af', fontSize: '0.75rem', lineHeight: 1.6, maxWidth: '200px', marginBottom: '14px' }}>
              India's premium salon booking platform — connecting customers with top salons.
            </p>

            {/* Social Icons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {socialLinks.map(({ icon, label, url, bg }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  style={{
                    width: '34px', height: '34px', borderRadius: '10px',
                    background: bg, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'white',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; }}
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <p style={headingStyle}>Company</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button style={linkStyle} onClick={() => navigate('/help')}
                onMouseEnter={e => e.target.style.color = '#dc2626'}
                onMouseLeave={e => e.target.style.color = dark ? '#9ca3af' : '#6b7280'}>
                Help Center
              </button>
              <button style={linkStyle} onClick={() => navigate('/about')}
                onMouseEnter={e => e.target.style.color = '#dc2626'}
                onMouseLeave={e => e.target.style.color = dark ? '#9ca3af' : '#6b7280'}>
                About Us
              </button>
              <button style={linkStyle} onClick={() => navigate('/privacy')}
                onMouseEnter={e => e.target.style.color = '#dc2626'}
                onMouseLeave={e => e.target.style.color = dark ? '#9ca3af' : '#6b7280'}>
                Privacy Policy
              </button>
              <button style={linkStyle} onClick={() => navigate('/terms')}
                onMouseEnter={e => e.target.style.color = '#dc2626'}
                onMouseLeave={e => e.target.style.color = dark ? '#9ca3af' : '#6b7280'}>
                Terms & Conditions
              </button>
            </div>
          </div>

          {/* Contact + Follow */}
          <div>
            <p style={headingStyle}>Get In Touch</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              <a href="mailto:bookyoursalons@gmail.com" style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
                onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                onMouseLeave={e => e.currentTarget.style.color = dark ? '#9ca3af' : '#6b7280'}>
                <Mail size={12} />
                bookyoursalons@gmail.com
              </a>
              <button style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={() => navigate('/salon/login')}
                onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                onMouseLeave={e => e.currentTarget.style.color = dark ? '#9ca3af' : '#6b7280'}>
                <Scissors size={12} />
                Become a Partner
              </button>
            </div>

            <p style={headingStyle}>Follow Us @bookyoursalons</p>
            <p style={{ color: '#6b7280', fontSize: '0.72rem', lineHeight: 1.6 }}>
              Don't miss offers, new salons & beauty tips — follow us everywhere!
            </p>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <p style={{ color: '#6b7280', fontSize: '0.72rem' }}>
              © {year} <strong style={{ color: '#9ca3af' }}>BookYourSalons Private Limited</strong>. All rights reserved.
            </p>
            <p style={{ color: '#4b5563', fontSize: '0.68rem' }}>
              Made with ❤️ in India 🇮🇳
            </p>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
